import React, {useEffect, useState} from "react";

import {
	Bill,
	BillLineList,
	BillRestControllerApiFactory,
	BudgetList,
	BudgetRestControllerApiFactory,
	CustomerRestControllerApiFactory
} from "arteco-api-client-ts";
import {FormContent} from "../../../../components/form/form_inputs/form_buttons";
import {FormInput} from "../../../../components/form/form_inputs/form_input";
import FormSelect from "../../../../components/form/form_inputs/form_select";
import {types} from "../../../../components/form/types";
import FormAutocomplete from "../../../../components/form/form_inputs/form_autocomplete";
import {
	Box,
	Button,
	Grid,
	MenuItem,
	Modal,
	Select,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableRow,
	TextField
} from "@mui/material";
import {useForm, UseFormProps} from "../../../../components/form/form_model";
import {fileLocalSave} from "../../../../components/form/form_inputs/form_file";
import {useApiManager} from "../../../../components/utils/api";
import {useNavigate} from "react-router-dom";
import {numberToString} from "../../../../components/utils/numbers";
import FormToggle from "../../../../components/form/form_inputs/form_toggle";
import {employeeModalStyle} from "../../../employee/modal";


export const useCustomerNotes = (_customerId: number | undefined = undefined) => {
	const apiManager = useApiManager();
	const customerCtrl = apiManager.factory(CustomerRestControllerApiFactory);
	const [customerNotes, setCustomerNotes] = useState<string>("");
	const [customerId, setCustomerId] = useState<number | undefined>(_customerId);

	const loadCustomerNotes = () => {
		if (customerId) {
			apiManager.executeRaw(customerCtrl.getCustomer(customerId)).then((resp) => {
				setCustomerNotes(resp.data?.observation || "");
			});
		} else {
			setCustomerNotes("");
		}
	}

	useEffect(() => {
		loadCustomerNotes();
	}, [customerId]);

	useEffect(() => {
		loadCustomerNotes();
	}, []);

	return {customerNotes, setCustomerId};
}

export const BillEditPage = () => {
	const navigate = useNavigate();
	const apiManager = useApiManager();

	const ctrl = apiManager.factory(BillRestControllerApiFactory);
	const budgetCtrl = apiManager.factory(BudgetRestControllerApiFactory);
	const {customerNotes, setCustomerId} = useCustomerNotes();
	const [selectLine, setSelectLine] = useState<BillLineList>();
	const [budgets, setBudgets] = useState<BudgetList[]>([]);

	useEffect(() => {
		apiManager.execute(budgetCtrl.listBudget()).then(resp => setBudgets(resp))
	}, []);

	const form = useForm({
		fetcher: (id) => {
			const promise = ctrl.getBill(id);
			promise.then(resp => setCustomerId(resp.data.customer.id));
			return promise;
		},
		updater: (bean) => ctrl.saveBill(bean),
		remover: (id) => ctrl.deleteOrDismissBill(id),
		factory: () => {
			return {
				template: {id: 50, name: "Factura Estándar"}
			} as Bill;
		},
		extraActions: [
			{
				label: "Duplicar",
				action: () => {
					const id = form.getId();
					if (confirm("Guarde lo cambios antes de duplicar.\n" +
						"La pantalla se posicionará sobre el registro nuevo.\n\n" +
						"¿Seguro que desea duplicar la factura?") && id) {
						apiManager.executeRaw(ctrl.duplicateBill(id)).then((resp) => {
							navigate("/app/admin/billing/bill/" + resp.data.id);
							window.location.reload();
						});
					}
				},
			},
			{
				label: "Pdf",
				action: () => {
					const id = form.getId();
					if (id) {
						apiManager.executeRaw(ctrl.viewPdfBill(id)).then((resp) => {
							fileLocalSave(resp);
						});
					}
				},
			},
			{
				label: "e-Factura",
				action: () => {
					const id = form.getId();
					if (id) {
						if (confirm("Esta opción descarga un XML que debe firmarse con AutoFirma, obteniendo un XSIG que debe subirse a Face. Deseas continuar?"))
							apiManager.executeRaw(ctrl.viewFacturaEBill(id)).then((resp) => {
								fileLocalSave(resp);
							});
					}
				},
			},
		],
	} as UseFormProps<Bill>);


	return (
		<>
			<FormContent form={form} title={"Formulario de factura"}>

				<Grid container spacing={3}>
					<Grid item sm={9}>
						<Grid container spacing={3}>
							<Grid item sm={3}>
								<FormInput type={"number"} field={"number"} label={"Número"}/>
							</Grid>
							<Grid item sm={3}>
								<FormInput field={"code"} label={"Código"}/>
							</Grid>
							<Grid item sm={3}>
								<FormSelect field={"template"} label={"Plantilla"} type={types.templates}/>
							</Grid>
							<Grid item sm={3}>
								<FormToggle field={"doubtful"} label={"Incobrable"}/>
							</Grid>
						</Grid>

						<Grid container spacing={3}>
							<Grid item sm={3}>
								<FormAutocomplete
									field={"customer"}
									label={"Cliente"}
									type={types.customers}
									onChange={(type, value) => setCustomerId(value?.id)}
								/>
							</Grid>
							<Grid item sm={6}>
								<FormAutocomplete
									field={"organizationUnit"}
									label={"Unidad"}
									type={types.organizationUnits}
								/>
							</Grid>
							<Grid item sm={3}>
								<FormToggle field={"avoidDiscounts"} label={"No descontable"}/>
							</Grid>
						</Grid>

						<Grid container spacing={3}>
							<Grid item sm>
								<FormInput field={"issueDate"} type={"date"} label={"Emisión"}/>
							</Grid>
							<Grid item sm>
								<FormInput field={"sentDate"} type={"date"} label={"Enviada"}/>
							</Grid>
							<Grid item sm>
								<FormInput field={"advanceDate"} type={"date"} label={"Anticipada"}/>
							</Grid>
							<Grid item sm>
								<FormInput field={"paymentDate"} type={"date"} label={"Pago"}/>
							</Grid>
						</Grid>

						<fieldset>
							<legend>FACE - Administración Pública</legend>
							<Grid container spacing={3}>
								<Grid item sm={3}>
									<FormInput field={"faceCodContable"} type={"text"} label={"Cod Contable"}
											   helperText={"Formato: A04035971"}/>
								</Grid>
								<Grid item sm={3}>
									<FormInput field={"faceCodGestor"} type={"text"} label={"Cod Gestor"}/>
								</Grid>
								<Grid item sm={3}>
									<FormInput field={"faceCodTramitadora"} type={"text"} label={"Cod Tramit."}/>
								</Grid>
								<Grid item sm={3}>
									<div>Si sólo se indica el primer campo, se usará el mismo en todas</div>
								</Grid>
							</Grid>

						</fieldset>

						<Grid container spacing={3} py={2}>
							<Grid item sm={12} md={6}>
								<FormInput field={"description"} type={"text"} label={"Descripción"} rows={3}/>
							</Grid>
							<Grid item sm={12} md={6}>
								<FormInput field={"comments"} type={"text"} label={"Comentarios"} rows={3}/>
							</Grid>
						</Grid>

						{form.getId() && (
							<>
								<Table>
									<TableHead>
										<TableRow>
											<TableCell width={"10%"}>Code</TableCell>
											<TableCell width={"20%"}>Presup</TableCell>
											<TableCell width={"40%"}>Descrip</TableCell>
											<TableCell width={"10%"}>Cantidad</TableCell>
											<TableCell width={"10%"}>Precio Neto</TableCell>
											<TableCell width={"10%"}>Precio IVa</TableCell>
										</TableRow>
									</TableHead>
									<TableBody>
										{form.value("lines")?.map((line: BillLineList) => (
											<TableRow key={"line-" + line.id}>
												<TableCell>
													<Button variant={"outlined"}
															onClick={() => setSelectLine(line)}>{line.code}</Button>
												</TableCell>
												<TableCell>{line.budget?.title}</TableCell>
												<TableCell>{line.concept}</TableCell>
												<TableCell>{line.quantity}</TableCell>
												<TableCell>{line.unitNetPrice}</TableCell>
												<TableCell>{line.taxType}</TableCell>
											</TableRow>
										))}
									</TableBody>
								</Table>

								<Button variant={"outlined"}
										sx={{mt: 1}}
										onClick={() => setSelectLine({bill: {id: form.getId()}} as BillLineList)}>
									Nueva
								</Button>

								<Modal
									open={selectLine != undefined}
									onClose={_ => setSelectLine(undefined)}>
									<Box sx={employeeModalStyle}>

										<TextField type={"number"} sx={{my: 1}} fullWidth label={"Código"}
												   value={selectLine?.code}
												   onChange={e => setSelectLine({
													   ...selectLine as BillLineList,
													   code: e.target.value
												   })}/>

										<Select fullWidth
												value={selectLine?.budget?.id}
												onChange={e => setSelectLine({
													...selectLine as BillLineList,
													budget: budgets.find(s => s.id === e.target.value)
												})}>
											<MenuItem value={''}></MenuItem>
											{budgets.map(b => (
												<MenuItem key={"budget-" + b.id} value={b.id}>
													({b.id}) {b.title} - {b.customer.shortName}
												</MenuItem>
											))}
										</Select>

										<TextField type={"text"} sx={{my: 1}} fullWidth label={"Concepto"}
												   value={selectLine?.concept}
												   onChange={e => setSelectLine({
													   ...selectLine as BillLineList,
													   concept: e.target.value
												   })}/>
										<TextField type={"number"} sx={{my: 1}} fullWidth label={"Cantidad"}
												   value={selectLine?.quantity}
												   onChange={e => setSelectLine({
													   ...selectLine as BillLineList,
													   quantity: parseFloat(e.target.value)
												   })}/>
										<TextField type={"number"} sx={{my: 1}} fullWidth label={"Precio Neto"}
												   value={selectLine?.unitNetPrice}
												   onChange={e => setSelectLine({
													   ...selectLine as BillLineList,
													   unitNetPrice: parseFloat(e.target.value)
												   })}/>
										<TextField type={"number"} sx={{my: 1}} fullWidth label={"IVA"}
												   value={selectLine?.taxType}
												   onChange={e => setSelectLine({
													   ...selectLine as BillLineList,
													   taxType: parseFloat(e.target.value)
												   })}/>

										<Button variant={"contained"}
												sx={{marginRight: "1em"}}
												onClick={() => {
													const line = selectLine as BillLineList;
													line.pos = (form.value("lines").length || 0) + 1;
													line.bill = {id: form.getId()} as Bill;
													apiManager
													.execute(ctrl.saveLineBill(form.getId() as number, line))
													.then(() => {
														setSelectLine(undefined);
														return form.load()
													});
												}}>
											Guardar
										</Button>

										<Button variant={"contained"}
												sx={{marginRight: "1em"}}
												onClick={() => {
													if (confirm("Seguro que deseas borrar la línea?")) {
														setSelectLine(undefined);
														apiManager
														.execute(ctrl.removeLinesBill(form.getId() as number, [(selectLine as BillLineList).id as number]))
														.then(resp => form.load());
													}
												}}>
											Borrar
										</Button>


									</Box>
								</Modal>

							</>
						)}
					</Grid>
					<Grid item sm={3}>

						{form.propModel.value?.totals && (
							<>
								<TextField
									sx={{mt: 0, mb: 1}}
									fullWidth
									inputProps={{style: {textAlign: "right"}}}
									label={"Cantidad"}
									value={numberToString(form.propModel.value?.totals.quantity)}
									disabled/>
								<TextField
									sx={{my: 1}}
									fullWidth
									inputProps={{style: {textAlign: "right"}}}
									label={"SubTotal"}
									value={numberToString(form.propModel.value?.totals.subTotal)}
									disabled/>
								<TextField
									sx={{my: 1}}
									fullWidth
									inputProps={{style: {textAlign: "right"}}}
									label={"IVA"}
									value={numberToString(form.propModel.value?.totals.taxes)}
									disabled/>
								<TextField
									sx={{my: 1}}
									fullWidth
									inputProps={{style: {textAlign: "right"}}}
									label={"Total"}
									value={numberToString(form.propModel.value?.totals.total)}
									disabled/>
							</>
						)}

						<TextField
							sx={{mt: 2}}
							fullWidth
							label={"Notas del cliente"}
							value={customerNotes}
							disabled
							minRows={5}
							multiline={true}
							inputProps={{style: {fontSize: "0.8em"}}}/>
					</Grid>
				</Grid>


			</FormContent>
		</>
	);
};